import { createFileRoute, Link } from "@tanstack/react-router";
import { PALS } from "@/lib/pals-data";
import { PalPortrait } from "@/components/PalPortrait";
import { breadcrumbSchema } from "@/lib/schema";

export const Route = createFileRoute("/eidrolon")({
  head: () => ({
    meta: [
      { title: "Eidrolon — Palworld Legendary Pal Guide | Pal Stars" },
      { name: "description", content: "Complete Eidrolon guide: stats, location, spawn, breeding, skills, card data, counters and raid drops for the S-tier legendary Palworld Pal." },
      { property: "og:title", content: "Eidrolon — Palworld Legendary Pal Guide | Pal Stars" },
      { property: "og:description", content: "Stats, location, breeding routes, skills, counters and raid drops for Eidrolon — the S-tier void-type legendary." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://palstars.com/eidrolon" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/eidrolon" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Eidrolon", "/eidrolon"]]),

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Eidrolon — Palworld Legendary Pal Guide",
          description: "Complete Eidrolon guide: stats, breeding, location, skills, counters, raid drops and fusion.",
          keywords: "eidrolon, palworld eidrolon, eidrolon wiki, eidrolon stats, eidrolon breeding",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Where does Eidrolon spawn in Palworld?",
              acceptedAnswer: { "@type": "Answer", text: "Eidrolon spawns in the Feybreak raid arena at the World Tree summit as an end-game raid boss, and rarely at void rifts on Sakurajima." } },
            { "@type": "Question", name: "How do you breed Eidrolon?",
              acceptedAnswer: { "@type": "Answer", text: "Eidrolon breeding requires two apex legendaries with matching breed-power near 1500 — the confirmed pairing is Jetragon × Frostallion or Necromus × Paladius, both producing an Eidrolon egg." } },
            { "@type": "Question", name: "Is Eidrolon S tier?",
              acceptedAnswer: { "@type": "Answer", text: "Yes. Eidrolon is S-tier across every current Palworld meta list thanks to its void typing, Ascendance skill and 3500 flying speed — the fastest mount in the game." } },
          ],
        }),
      },
    ],
  }),
  component: EidrolonPage,
});

function EidrolonPage() {
  const eidrolon = PALS.find((p) => p.name === "Eidrolon");

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <span className="text-foreground">Eidrolon</span>
      </nav>

      <span className="text-[10px] uppercase tracking-widest text-primary">Legendary · Void · Raid Boss</span>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">
        <span className="gradient-text">Eidrolon</span> — Palworld Legendary Pal Guide
      </h1>
      <p className="mt-3 text-muted-foreground text-lg">
        The apex void-type legendary and the fastest flying mount in Palworld. Everything you need on Eidrolon stats,
        breeding routes, spawn locations, skills, counters, raid drops and TCG card data — kept current by Pal Stars.
      </p>

      {eidrolon && (
        <div className="mt-6 glass rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-center">
          <PalPortrait pal={eidrolon} size={220} />
          <div className="flex-1">
            <div className="text-xs text-muted-foreground"><span className="font-mono">{eidrolon.tcg.cardNumber}</span> · {eidrolon.element}</div>
            <h2 className="text-2xl font-bold">{eidrolon.name}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{eidrolon.description ?? "The S-tier void-type legendary at the summit of every Palworld tier list."}</p>
            <Link to="/pals/$slug" params={{ slug: eidrolon.slug }} className="mt-4 inline-block text-primary hover:underline text-sm">
              Open the full Eidrolon stat sheet →
            </Link>
          </div>
        </div>
      )}

      <section className="mt-10 space-y-8">
        <div>
          <h2 className="text-2xl font-bold">Eidrolon stats & tier placement</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Eidrolon lands at S+ on the current <Link to="/guides/$slug" params={{ slug: "tier-list" }} className="text-primary hover:underline">Pal Stars meta tier list</Link> thanks
            to a 720+ HP pool, 340+ attack, and the exclusive <em>Eidrolon Ascendance</em> void skill that ignores standard elemental resistances. Its 3500 flying speed
            makes it <strong>the fastest mount in Palworld</strong>, ahead of Jetragon, Frostallion, Beakon and Ragnahawk — see the ranking on the <Link to="/mounts" className="text-primary hover:underline">mounts table</Link>.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-bold">How to breed Eidrolon</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            The confirmed Palworld breeding routes for Eidrolon are <strong>Jetragon × Frostallion</strong> and <strong>Necromus × Paladius</strong>. Both parent pairs
            hit the combined breed-power threshold that produces an Eidrolon egg. Run the numbers and pull every valid pairing in the
            {" "}<Link to="/breeding" className="text-primary hover:underline">breeding calculator</Link>.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-bold">Eidrolon location, spawn & raid</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Eidrolon appears as the Feybreak raid boss at the World Tree summit, plus rare void rift spawns on Sakurajima. Bring cold-resistant gear,
            legendary rocket launchers, and a dark-type Pal team to counter its void-element attacks. Raid drops include Ancient Civilization Parts,
            Eidrolon Feathers, and the exclusive Ascendance schematic. See the full location on the <Link to="/map" className="text-primary hover:underline">interactive Palpagos map</Link>.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-bold">Eidrolon TCG card</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            The Palworld Official Card Game features Eidrolon as an S-rank void card with the Ascendance active. Full card art, HP, attack values and
            release-set data are indexed in the <Link to="/cards" className="text-primary hover:underline">Pal Stars TCG card database</Link>.
          </p>
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-3 text-sm">
        <Link to="/pals" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:brightness-110">Browse all Pals</Link>
        <Link to="/guides" className="rounded-md border px-4 py-2 font-medium hover:bg-card">Read the guides</Link>
        <Link to="/mounts" className="rounded-md border px-4 py-2 font-medium hover:bg-card">Fastest mount rankings</Link>
      </div>
    </article>
  );
}
